import React, { FC } from 'react';
import { Redirect } from 'react-router-dom';
import { Alert, Button } from 'reactstrap';
import { HotelPublicInformation } from '../../../shared/hotel';
import { MaterialIcon, PageHeader, TermsLoader, UserStateComponent } from '../../components';
import { MaterialIconText } from '../../components/MaterialIconText';
import { useTranslation } from '../../translations';
import { Fetcher, isResourceError, useFetcher } from '../../utils';
import { captureError } from '../../utils/errorHandling';
import { MyReservationWidget } from './MyReservationWidget';
import { WaveList } from './WaveList';

import './dashboard.scss';

export const HotelDashboard: FC = () => {
  const { ts } = useTranslation();

  const waveFetcher = useFetcher(async () => {
    try {
      return await api.getPublicHotelReleases();
    } catch (error) {
      if (!isResourceError(error)) {
        captureError(error as Error);
      }

      throw error;
    }
  });

  if (waveFetcher.error && isResourceError(waveFetcher.error, 'HotelIntegration')) {
    return <Redirect to="/" />;
  }

  if (!waveFetcher.success) {
    return <Fetcher result={waveFetcher} />;
  }

  return (
    <UserStateComponent>
      <PageHeader>{ts('hotel_reservations')}</PageHeader>
      <div className="hotel-dashboard">
        <div className="hotel-dashboard-actions">
          <MyReservationWidget info={waveFetcher.data} refresh={waveFetcher.refresh} />
          <GroupBookingWidget info={waveFetcher.data} />
        </div>
        <hr />
        <WaveList info={waveFetcher.data} />
        <hr />
        <Alert color="info" fade={false}>
          <strong>{ts('event_hotel_reservation_policies')}</strong>
          <TermsLoader name="hotelReservationTerms" />
        </Alert>
      </div>
    </UserStateComponent>
  );
};

const GroupBookingWidget: FC<{ readonly info: HotelPublicInformation }> = ({ info }) => {
  const { ts } = useTranslation();

  if (!info.allowGroupBookings) {
    return <div className="action-spacer" />;
  }

  return (
    <div>
      <div className="action-title">
        <MaterialIcon large name="groups" />
        <span>{ts('my_group')}</span>
      </div>
      <div className="action-divider" />
      <div className="action-content text-small">
        <div className="action-content-divider" />
        <p className="text-center">{ts('group_reservations_are_not_enabled')}</p>
        <div className="action-content-divider" />
        <div className="action-content-button-list">
          <Button block color="primary" disabled outline>
            <MaterialIconText name="group_add">{ts('create_group')}</MaterialIconText>
          </Button>
          <Button block color="secondary" disabled outline>
            <MaterialIconText name="groups">{ts('join_existing_group')}</MaterialIconText>
          </Button>
        </div>
      </div>
    </div>
  );
};
